import type { LegacyState } from "./state-codec";

const DEFAULT_DATABASE_NAME = "second-brain-assets-v1";
const DATABASE_VERSION = 5;
const STATE_STORE = "state";
const STATE_KEY = "current";
const APP_KEY = "personal_life_os_v2";
const MIRROR_LIMIT = 2_000_000;
const USER_STORAGE_KEYS = [
  APP_KEY,
  "personal_life_os_v2_pending",
  "personal_life_os_v2_backend",
  "personal_life_os_backups",
  "second_brain_sync_device_v1",
] as const;

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type LegacyStorageOptions = Readonly<{
  databaseName?: string;
  factory?: IDBFactory;
  storage?: KeyValueStorage;
}>;

type StateRow = Readonly<{
  id: "current";
  data: string;
  savedAt: number;
  revision: number;
  writerId: string;
}>;

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("Legacy IndexedDB request failed")),
      { once: true },
    );
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "error",
      () => reject(transaction.error ?? new Error("Legacy cache transaction failed")),
      { once: true },
    );
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new Error("Legacy cache transaction aborted")),
      { once: true },
    );
  });
}

async function openDatabase(
  databaseName: string,
  factory: IDBFactory,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(databaseName, DATABASE_VERSION);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      const stores: Array<readonly [string, IDBObjectStoreParameters | undefined]> = [
        ["photos", { keyPath: "id" }],
        ["settings", undefined],
        ["backups", { keyPath: "ts" }],
        [STATE_STORE, { keyPath: "id" }],
        ["taskFiles", { keyPath: "id" }],
      ];
      for (const [name, options] of stores) {
        if (!database.objectStoreNames.contains(name)) {
          database.createObjectStore(name, options);
        }
      }
    });
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("Legacy cache could not be opened")),
      { once: true },
    );
    request.addEventListener(
      "blocked",
      () => reject(new Error("Legacy cache upgrade is blocked")),
      { once: true },
    );
  });
}

function parseState(serialized: string | null): LegacyState | null {
  if (serialized === null) return null;
  try {
    const value: unknown = JSON.parse(serialized);
    if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
    return value as LegacyState;
  } catch {
    return null;
  }
}

function stateSavedAt(state: LegacyState | null): number {
  if (state === null || typeof state.meta !== "object" || state.meta === null) return 0;
  const savedAt = Reflect.get(state.meta, "savedAt");
  return Number.isFinite(Number(savedAt)) ? Number(savedAt) : 0;
}

function resolvedOptions(options: LegacyStorageOptions): Required<LegacyStorageOptions> {
  return {
    databaseName: options.databaseName ?? DEFAULT_DATABASE_NAME,
    factory: options.factory ?? indexedDB,
    storage: options.storage ?? localStorage,
  };
}

export async function readLegacyState(
  options: LegacyStorageOptions = {},
): Promise<LegacyState | null> {
  const resolved = resolvedOptions(options);
  const mirror = parseState(resolved.storage.getItem(APP_KEY));
  let database: IDBDatabase | null = null;
  try {
    database = await openDatabase(resolved.databaseName, resolved.factory);
    const transaction = database.transaction(STATE_STORE, "readonly");
    const row = await requestResult(
      transaction.objectStore(STATE_STORE).get(STATE_KEY) as IDBRequest<StateRow | undefined>,
    );
    await transactionDone(transaction);
    const stored = parseState(row?.data ?? null);
    return stateSavedAt(stored) >= stateSavedAt(mirror) ? stored : mirror;
  } catch {
    return mirror;
  } finally {
    database?.close();
  }
}

export async function writeLegacyState(
  state: LegacyState,
  options: LegacyStorageOptions = {},
): Promise<void> {
  const resolved = resolvedOptions(options);
  const serialized = JSON.stringify(state);
  const savedAt = stateSavedAt(state) || Date.now();
  const revision =
    typeof state.meta === "object" && state.meta !== null
      ? Number(Reflect.get(state.meta, "revision")) || 0
      : 0;
  const writerId =
    typeof state.meta === "object" && state.meta !== null
      ? String(Reflect.get(state.meta, "writerId") ?? "cloud-bootstrap")
      : "cloud-bootstrap";

  const database = await openDatabase(resolved.databaseName, resolved.factory);
  try {
    const transaction = database.transaction(STATE_STORE, "readwrite");
    const row: StateRow = {
      id: STATE_KEY,
      data: serialized,
      savedAt,
      revision,
      writerId,
    };
    transaction.objectStore(STATE_STORE).put(row);
    await transactionDone(transaction);
  } finally {
    database.close();
  }

  if (serialized.length < MIRROR_LIMIT) resolved.storage.setItem(APP_KEY, serialized);
  else resolved.storage.removeItem(APP_KEY);
  resolved.storage.setItem("personal_life_os_v2_backend", "idb");
}

export async function clearLegacyDeviceCache(
  options: LegacyStorageOptions = {},
): Promise<void> {
  const resolved = resolvedOptions(options);
  for (const key of USER_STORAGE_KEYS) resolved.storage.removeItem(key);
  await new Promise<void>((resolve, reject) => {
    const request = resolved.factory.deleteDatabase(resolved.databaseName);
    request.addEventListener("success", () => resolve(), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("Legacy cache could not be cleared")),
      { once: true },
    );
    request.addEventListener("blocked", () => reject(new Error("Legacy cache clear blocked")), {
      once: true,
    });
  });
}
